/**
 * Mobile Product Card Modal
 * Handles variant selection modal for mobile devices
 * Leverages theme's native product-form custom element for 100% consistency
 */

class MobileProductCardModal {
  constructor() {
    this.activeModal = null;
    //this.scrollPosition = 0;
    this.init();
  }

  init() {
    this.bindEvents();
    this.setupThemeIntegration();
  }

  setupThemeIntegration() {
    // Get theme's cart drawer for integration
    this.cartDrawer = document.querySelector("m-cart-drawer");
    this.cartPage = document.querySelector("m-cart");

    // Subscribe to theme's cart events
    this.cartUpdateUnsubscriber = MinimogEvents.subscribe(
      MinimogTheme.pubSubEvents.cartUpdate,
      (data) => {
        this.handleCartUpdate(data);
      }
    );
  }

  bindEvents() {
    // Handle mobile trigger clicks (plus icon and add button)
    document.addEventListener("click", (e) => {
      const trigger = e.target.closest("[data-mobile-variant-trigger]");
      if (trigger) {
        e.preventDefault();
        e.stopPropagation();
        const productId = trigger.dataset.productId;
        this.openModal(productId);
      }

      // Handle modal close
      const closeButton = e.target.closest("[data-modal-close]");
      if (closeButton) {
        e.preventDefault();
        this.closeModal();
      }

      // Handle add to cart in modal - use theme's product-form
      const addButton = e.target.closest("[data-mobile-add-to-cart]");
      if (addButton) {
        e.preventDefault();
        this.handleAddToCartViaThemeForm(addButton);
      }
    });

    // Variant selection is handled by product-card-option.liquid
    // No need for duplicate event handling here

    // Close modal on escape key
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && this.activeModal) {
        this.closeModal();
      }
    });
  }

  openModal(productId) {
    const modal = document.querySelector(
      `[data-mobile-modal][data-product-id="${productId}"]`
    );
    if (!modal) return;

    this.activeModal = modal;

    // Preserve scroll position
    this.scrollPosition =
      window.pageYOffset || document.documentElement.scrollTop;

    // Show modal with animation
    modal.style.display = "flex";
    document.body.classList.add("modal-open");

    // Apply scroll position preservation
    document.body.style.top = `-${this.scrollPosition}px`;

    // Trigger animation
    requestAnimationFrame(() => {
      modal.setAttribute("data-show", "");
    });

    // Reset form state
    this.resetModalForm(modal);
  }

  closeModal() {
    if (!this.activeModal) return;

    const modal = this.activeModal;

    // Hide modal with animation
    modal.removeAttribute("data-show");

    setTimeout(() => {
      modal.style.display = "none";
      document.body.classList.remove("modal-open");

      // Restore scroll position
      document.body.style.top = "";
      window.scrollTo(0, this.scrollPosition);

      this.activeModal = null;
    }, 300);
  }

  resetModalForm(modal) {
    const form = modal.querySelector("form");
    if (!form) return;

    // Uncheck all radio buttons
    const radioButtons = form.querySelectorAll('input[type="radio"]');
    radioButtons.forEach((radio) => (radio.checked = false));

    // Disable add to cart button
    const addButton = form.querySelector("[data-mobile-add-to-cart]");
    if (addButton) {
      addButton.disabled = true;
    }

    // Clear variant input
    const variantInput = form.querySelector('input[name="id"]');
    if (variantInput) {
      variantInput.value = "";
      variantInput.disabled = true;
    }
  }

  // Variant selection logic moved to product-card-option.liquid
  // This eliminates duplication and maintains single source of truth

  /**
   * Use theme's native product-form custom element for submission
   * This ensures 100% consistency with main product form behavior
   */
  handleAddToCartViaThemeForm(button) {
    if (button.disabled) return;

    const form = button.closest("form");
    const productForm = form.closest("product-form");

    if (!form || !productForm) {
      console.error("Could not find product-form custom element");
      return;
    }

    const variantInput = form.querySelector('input[name="id"]');
    if (!variantInput || !variantInput.value) {
      // Let theme handle the error - it will show variant selection error
      this.triggerThemeFormSubmission(form);
      return;
    }

    // Set up one-time event listeners for this submission
    const handleSuccess = () => {
      // Close modal on successful submission
      this.closeModal();

      // Clean up listeners
      productForm.removeEventListener("submit", handleFormSubmit);
      document.removeEventListener("cart-update", handleSuccess);
    };

    const handleFormSubmit = (e) => {
      // Let theme handle the actual submission
      // We just listen for the cart update event to close modal
      document.addEventListener("cart-update", handleSuccess, { once: true });
    };

    // Listen for form submission
    productForm.addEventListener("submit", handleFormSubmit, { once: true });

    // Trigger the theme's form submission
    this.triggerThemeFormSubmission(form);
  }

  /**
   * Trigger the theme's native form submission
   * This will use all the theme's built-in logic for validation, errors, cart updates, etc.
   */
  triggerThemeFormSubmission(form) {
    // Create and dispatch a submit event on the form
    // The theme's product-form custom element will handle everything
    const submitEvent = new Event("submit", {
      bubbles: true,
      cancelable: true,
    });

    form.dispatchEvent(submitEvent);
  }

  handleCartUpdate(data) {
    // Handle cart updates from theme's cart system
    // This ensures our modal stays in sync with cart changes
    //console.log("Cart updated:", data);
  }

  // Clean up subscriptions when component is destroyed
  disconnect() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }
}

// Shared utilities removed - now using theme's variant picker system

// Initialize the modal system
document.addEventListener("DOMContentLoaded", () => {
  window.MobileProductCardModal = new MobileProductCardModal();
});
